import React from "react";

import { faCircleNotch } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { groupBy } from "lodash-es";
import { AnimatePresence, m } from "motion/react";

import { Divider } from "src/base-components/Divider";
import { Pill } from "src/base-components/Pill";
import {
  UnpublishedFlowVersion,
  useUnpublishedFlowVersions,
} from "src/cases/templates/useUnpublishedFlowVersions";
import { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion } from "src/clients/case-management";
import { Callout } from "src/design-system/Callout";
import { IconLink } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ArchivedPill, DraftPill } from "src/flow/Pills";
import {
  DashboardPageParams,
  getBaseUrl,
  getUrlToAuthoringPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type UnpublishedFlowVersionsWarningProps = {
  version: CaseTemplateVersion | null;
};

const appearAnimation = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.15 },
} as const;

export const UnpublishedFlowVersionsWarning: React.FC<
  UnpublishedFlowVersionsWarningProps
> = ({ version }) => {
  const { unpublishedFlowVersions, isChecking, hasFailedChecks } =
    useUnpublishedFlowVersions(version);

  if (!isChecking && !hasFailedChecks && unpublishedFlowVersions.length === 0)
    return null;

  return (
    <div
      className="flex flex-col gap-y-3"
      data-loc="unpublished-flow-versions-warning"
    >
      <Divider />
      <AnimatePresence initial={false}>
        {unpublishedFlowVersions.length > 0 && (
          <m.div key="warning" className="overflow-hidden" {...appearAnimation}>
            <Callout variant="warning">
              You can publish this template now and update its Decision Flows
              later. To make sure Cases run successfully, publish the connected
              Decision Flows too.
            </Callout>
          </m.div>
        )}
        <div className="decideScrollbar max-h-80 space-y-3">
          {Object.entries(groupBy(unpublishedFlowVersions, "flowSlug")).map(
            ([flowSlug, flowVersions]) => (
              <m.ul
                key={`flow-${flowSlug}`}
                className="space-y-2 overflow-hidden"
                {...appearAnimation}
              >
                <li className="font-inter-semibold-13px text-gray-800">
                  {flowVersions[0].flowName ?? flowSlug}
                </li>
                {flowVersions.map((flowVersion) => (
                  <FlowVersionEntry
                    key={`${flowSlug}-${flowVersion.versionName}`}
                    version={flowVersion}
                    flowSlug={flowSlug}
                  />
                ))}
              </m.ul>
            ),
          )}
          {isChecking && (
            <m.div
              key="checking"
              className="overflow-hidden"
              {...appearAnimation}
            >
              <Callout icon={faCircleNotch} variant="neutral" isIconSpinning>
                Checking for unpublished Decision Flows
              </Callout>
            </m.div>
          )}
          {hasFailedChecks && (
            <m.div
              key="failed-checks"
              className="overflow-hidden"
              {...appearAnimation}
            >
              <Callout variant="warning">
                Some connected Decision Flows could not be checked. To make sure
                Cases run successfully, verify they are published
              </Callout>
            </m.div>
          )}
        </div>
      </AnimatePresence>
    </div>
  );
};

const FlowVersionEntry: React.FC<{
  version: UnpublishedFlowVersion;
  flowSlug: string;
}> = ({ version, flowSlug }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  return (
    <li
      key={`${flowSlug}-${version.versionName}`}
      className="flex items-center gap-x-2 border-b border-b-transparent [&:not(:last-child)]:border-gray-200"
    >
      <span className="mr-auto font-inter-normal-13px text-gray-500">
        {version.versionName}
      </span>
      {version.status === "draft" && (
        <DraftPill
          dataLoc={`draft-pill-${flowSlug}-${version.versionName}`}
          size="sm"
          withIcon
        />
      )}
      {version.status === "archived" && (
        <ArchivedPill
          dataLoc={`archived-pill-${flowSlug}-${version.versionName}`}
          size="sm"
          withIcon
        />
      )}
      {version.status === "not-found" && (
        <Tooltip
          placement="bottom-end"
          title="Flow not found — it may be archived or missing from this workspace"
          asChild
        >
          <Pill
            dataLoc={`not-found-pill-${flowSlug}-${version.versionName}`}
            size="sm"
            variant="gray"
          >
            <Pill.Text>Not Found</Pill.Text>
          </Pill>
        </Tooltip>
      )}
      {version.flowId && version.versionId && (
        <Tooltip placement="bottom-end" title="Open in new tab" asChild>
          <IconLink
            aria-label="Open in new tab"
            href={
              getBaseUrl() +
              getUrlToAuthoringPage(
                orgId,
                wsId,
                version.flowId,
                version.versionId,
              )
            }
            icon={faUpRightFromSquare}
            openInNewTab
            size="xs"
          />
        </Tooltip>
      )}
    </li>
  );
};
